import 'react';
import {useState, useMemo} from 'react';
import {Box, ListItemIcon, MenuItem, Skeleton} from '@mui/material';
import {DashboardMenuItem, MenuItemLink, MenuProps, useSidebarState} from 'react-admin';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import clsx from 'clsx';
import {filterMenuForSidebar} from '@/utils/menuUtils';
import {useAuthMenu} from '@/services/authService/authMenu';
import type {Menu as MenuType} from '@/services/manageService/menuService';
import SubMenu from '@/pages/layout/SubMenu';
import {resolveMenuIcon} from '@/assets/icons/menuIconMap';
import {toRealmRoutePath} from '@/router/attactComponent';

/** Staggered text widths so the bone reads like real labels, not a brick wall. */
const MENU_SKELETON_TEXT_WIDTHS = ['68%', '55%', '78%', '62%', '72%', '48%', '60%'] as const;

/**
 * Sidebar menu bone: reuse MenuItem + ListItemIcon so gutters/height match live items.
 */
function MenuSkeleton({open, dense}: {open: boolean; dense: boolean}) {
    return (
        <Box aria-busy="true" aria-label="Loading menu">
            {MENU_SKELETON_TEXT_WIDTHS.map((width, index) => (
                <MenuItem
                    key={`menu-skeleton-${index}`}
                    dense={dense}
                    disableRipple
                    tabIndex={-1}
                    sx={{
                        pointerEvents: 'none',
                        // Match softTheme RaMenuItemLink left rail so icon/text align with live links
                        borderLeft: '3px solid transparent',
                        '& .MuiListItemIcon-root': {
                            minWidth: theme =>
                                open ? theme.spacing(5) : theme.spacing(2),
                        },
                    }}
                >
                    <ListItemIcon>
                        <Skeleton variant="circular" width={20} height={20} animation="wave" />
                    </ListItemIcon>
                    {open ? (
                        <Skeleton
                            variant="rounded"
                            animation="wave"
                            height={12}
                            width={width}
                            sx={{borderRadius: 0.5}}
                        />
                    ) : null}
                </MenuItem>
            ))}
        </Box>
    );
}

const Menu = ({dense = false}: MenuProps) => {
    const [open] = useSidebarState();
    const {data: menuTree = [], isLoading} = useAuthMenu();
    const menus = useMemo(() => filterMenuForSidebar(menuTree as MenuType[]), [menuTree]);
    const [openMap, setOpenMap] = useState<{[key: string]: boolean}>({});

    const handleToggle = (id: string | number) => {
        setOpenMap(prev => ({...prev, [id]: !prev[id]}));
    };

    const renderMenus = (items: MenuType[], depth = 0) =>
        items.map(menu => {
            const IconComponent = resolveMenuIcon(menu.menuIcon);
            if (menu.children && menu.children.length > 0) {
                return (
                    <SubMenu
                        key={menu.menuId}
                        dense={dense}
                        depth={depth}
                        handleToggle={() => handleToggle(menu.menuId)}
                        isOpen={!!openMap[menu.menuId]}
                        name={menu.menuName}
                        icon={<IconComponent fontSize="small" />}
                    >
                        {renderMenus(menu.children, depth + 1)}
                    </SubMenu>
                );
            }

            // Leaf: browser path = realm_url (full tree path)
            const toPath = toRealmRoutePath(
                menu.realmUrl,
                menu.realm,
                menu.menuId,
            );

            return (
                <MenuItemLink
                    key={menu.menuId}
                    to={toPath}
                    state={{_scrollToTop: true}}
                    primaryText={menu.menuName}
                    leftIcon={<IconComponent fontSize="small" />}
                    dense={dense}
                />
            );
        });

    return (
        <Box
            sx={{
                width: open ? 200 : 50,
                marginTop: 1,
                marginBottom: 1,
                transition: theme =>
                    theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
            }}
            className={clsx({
                'RaMenu-open': open,
                'RaMenu-closed': !open,
            })}
        >
            {isLoading ? (
                <MenuSkeleton open={open} dense={dense} />
            ) : (
                <>
                    <DashboardMenuItem leftIcon={<DashboardOutlinedIcon fontSize="small" />} />
                    {renderMenus(menus)}
                </>
            )}
        </Box>
    );
};

export default Menu;
